import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Dispatch } from "redux";
import styled from "styled-components";
import { AddButton, ButtonRow } from "../../../Common/Components/FormComponents";
import { Failure, FailureHeader, FailureMessage, Success, SuccessMessage } from "../../../Common/Styles/Alert";
import { Section, SectionTitle } from "../../../Common/Styles/containers";
import { IState } from "../../../reducer";
import { ICompleteOrganization, RequestState } from "../../../types/common";
import * as actions from "../actions";
import { IMiddleError, IProgressSteps, ReportRequested } from "../types";

const Progress = styled.progress`
  width: 50%;
`;

type ReportsProps = IReportsOwnProps & IReportsStateToProps & IReportsDispatchProps;

interface IReportsOwnProps {
  accountId: string;
  organization: ICompleteOrganization;
}

interface IReportsStateToProps {
  inProgress: boolean;
  hasFinished: boolean;
  fatalErrorFound: boolean;
  middleErrors: IMiddleError[];
  requestedReport: ReportRequested | null;
  stepData: IProgressSteps;
}

interface IReportsDispatchProps {
  requestAllUserData: () => void;
  requestAllAssessmentData: () => void;
}

const MiddleError: React.FC<{ middleError: IMiddleError }> = props => (
  <li>
    <h4>
      <Link to={`/scorm-packages/${props.middleError.scormPackageId}`}>
        {props.middleError.scormPackageName || "Unknown package"}
      </Link>
    </h4>
    <p>
      <strong>Reason:</strong> {props.middleError.message}
    </p>
  </li>
);

type ReportStatusProps = IReportsStateToProps;

const ReportStatus: React.FC<ReportStatusProps> = props => {
  const { fatalErrorFound, hasFinished, inProgress, middleErrors, stepData } = props;
  return (
    <div>
      {fatalErrorFound && (
        <Failure>
          <FailureHeader>Fatal Error</FailureHeader>
          <FailureMessage>Could not get the list of scorm package IDs to process the report.</FailureMessage>
        </Failure>
      )}
      {middleErrors.length > 0 && (
        <Failure>
          <p>We found some errors when generating the report:</p>
          <ul>
            {middleErrors.map((middleError, idx) => (
              <MiddleError key={idx} middleError={middleError} />
            ))}
          </ul>
        </Failure>
      )}
      {inProgress && stepData.totalSteps !== 0 && (
        <div>
          <Progress value={stepData.stepNumber} max={stepData.totalSteps} />
          <p>
            Report progress: {stepData.stepNumber}/{stepData.totalSteps} scorm packages processed.
          </p>
        </div>
      )}
      {hasFinished && stepData.totalSteps === stepData.stepNumber && (
        <Success>
          <SuccessMessage>Report generation has finished.</SuccessMessage>
        </Success>
      )}
    </div>
  );
};

const Reports: React.FC<ReportsProps> = props => {
  const { inProgress, requestAllAssessmentData, requestAllUserData, requestedReport } = props;
  return (
    <Section>
      <SectionTitle>Request All User Data</SectionTitle>

      <ButtonRow margin={1} width={15}>
        <AddButton disabled={inProgress} onClick={requestAllUserData}>
          Export All Table Data
        </AddButton>
      </ButtonRow>

      {requestedReport === "users" && <ReportStatus {...props} />}

      <SectionTitle>Request All Assessment Data</SectionTitle>

      <ButtonRow margin={1} width={22}>
        <AddButton disabled={inProgress} onClick={requestAllAssessmentData}>
          Export All Assessment Data
        </AddButton>
      </ButtonRow>

      {requestedReport === "assessments" && <ReportStatus {...props} />}
    </Section>
  );
};

const mapStateToProps = (state: IState): IReportsStateToProps => ({
  fatalErrorFound: state.App.OrganizationSettings.requestAllTableDataStatus === RequestState.FAILED,
  hasFinished: state.App.OrganizationSettings.requestAllTableDataStatus === RequestState.SUCCEEDED,
  inProgress: state.App.OrganizationSettings.requestAllTableDataStatus === RequestState.PENDING,
  middleErrors: state.App.OrganizationSettings.middleErrors,
  requestedReport: state.App.OrganizationSettings.requestedReport,
  stepData: state.App.OrganizationSettings.stepData,
});
const mapDispatchToProps = (dispatch: Dispatch): IReportsDispatchProps => ({
  requestAllAssessmentData: () => dispatch(actions.requestAllAssessmentData()),
  requestAllUserData: () => dispatch(actions.requestAllUserData()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Reports);
